// Public URL tails can be secrets of any shape, including human-readable tokens.
const PUBLIC_ROUTE_LABELS: Record<string, string> = {
  sign: 'Sign document',
  verify: 'Verify document',
  feedback: 'Feedback',
  portal: 'Portal',
  upload: 'Upload documents',
  unsubscribe: 'Email preferences',
  whistleblower: 'Whistleblower report',
  invite: 'Invitation',
  'reset-password': 'Reset password',
};

const APP_TITLE = 'Statura Care';

const SEGMENT_LABELS: Record<string, string> = {
  acp: 'Advance care plan',
  admin: 'Admin',
  'advance-care-plans': 'Advance care plans',
  'ai-usage': 'AI usage',
  'at-hm': 'AT-HM',
  'an-acc': 'AN-ACC',
  auth: 'Auth',
  availability: 'Availability',
  billing: 'Billing',
  budgets: 'Budgets',
  capabilities: 'Capabilities',
  'care-pathways': 'Care pathways',
  'care-plans': 'Care plans',
  claims: 'Claims',
  clients: 'Clients',
  'cm-task-types': 'Care-management task types',
  compliance: 'Compliance',
  configuration: 'Configuration',
  contractors: 'Contractors',
  dashboard: 'Dashboard',
  devices: 'Devices',
  documents: 'Documents',
  'document-templates': 'Document templates',
  exceptions: 'Exceptions',
  family: 'Family portal',
  finance: 'Finance',
  handovers: 'Handovers',
  'home-care': 'Home care',
  hr: 'HR',
  invoices: 'Invoices',
  leave: 'Leave',
  live: 'Live',
  instances: 'Workflow runs',
  mar: 'MAR',
  medications: 'Medications',
  modules: 'Modules',
  monitoring: 'Monitoring',
  notifications: 'Notifications',
  onboarding: 'Onboarding',
  'onboarding-packs': 'Onboarding packs',
  'open-shifts': 'Open shifts',
  payroll: 'Payroll',
  periods: 'Periods',
  policies: 'Policies',
  portal: 'Portal',
  pricing: 'Pricing',
  profile: 'Profile',
  quality: 'Quality',
  reconciliation: 'Reconciliation',
  regions: 'Regions',
  reports: 'Reports',
  residential: 'Residential',
  roster: 'Roster',
  sah: 'SAH',
  schedules: 'Schedules',
  security: 'Security',
  settings: 'Settings',
  'service-areas': 'Service areas',
  'service-delivery': 'Service delivery',
  'signature-templates': 'Signature templates',
  signatures: 'Documents and signatures',
  staff: 'Staff',
  statements: 'Statements',
  subscriptions: 'Subscriptions',
  system: 'System',
  tasks: 'Tasks',
  telehealth: 'Telehealth',
  templates: 'Templates',
  timesheets: 'Timesheets',
  training: 'Training',
  users: 'Users',
  workflows: 'Workflows',
  workforce: 'Workforce',
  wounds: 'Wounds',
};

const ROUTE_LABELS: Record<string, string> = {
  '/': 'Home',
  '/overview': 'Home',
  '/activity': 'Activity',
  '/clients/new': 'New person',
  '/home-care/classifications': 'Home care classifications',
  '/compliance/quality-indicators?tab=submit': 'Submission data',
  '/clients/[id]/clinical/thresholds': 'Observation thresholds',
  '/home-care/service-delivery': 'Service delivery',
  '/medications/reconciliation/new': 'Medication reconciliation',
  '/home-care/budgets': 'Home care budgets',
  '/home-care/funding-changes': 'Client funding changes',
  '/home-care/claims': 'Home care claims',
  '/home-care/statements': 'Home care statements',
  '/compliance/sah-evidence': 'SAH evidence oversight',
  '/medications/mar': 'Medication administration record',
  '/roster/open-shifts': 'Open shifts',
  '/workflows/instances': 'Workflow runs',
};

function isLikelyId(segment: string): boolean {
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
    /^[0-9a-f]{20,}$/i.test(segment) ||
    /^\d+$/.test(segment)
  );
}

function sentenceCase(segment: string): string {
  return segment
    .replace(/[-_]+/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
}

/**
 * The human label for a route, with no app-title suffix — e.g. "Roster - Periods".
 * Dynamic segments are dropped or named (see isLikelyId below), so a raw UUID
 * never reaches user-facing copy.
 */
export function pageLabelFromPathname(pathname: string | null | undefined): string {
  const rawPath = (pathname || '/').split('?')[0] ?? '/';
  const path = rawPath.replace(/\/+$/, '') || '/';
  const first = path.split('/')[1] ?? '';
  if (Object.hasOwn(PUBLIC_ROUTE_LABELS, first)) return PUBLIC_ROUTE_LABELS[first]!;
  if (path === '/pay-documents/access' || path.startsWith('/pay-documents/access/'))
    return 'Access documents';
  if (path.startsWith('/auth/mobile-session') || path.startsWith('/auth/auth/mobile-session'))
    return 'Sign in';
  if (path.startsWith('/auth/recovery') || path.startsWith('/auth/auth/recovery'))
    return 'Account recovery';
  const exact = ROUTE_LABELS[path];
  if (exact) return exact;

  const labels = path
    .split('/')
    .filter(Boolean)
    .map((segment, index, segments) => {
      if (isLikelyId(segment)) {
        const previous = segments[index - 1];
        if (previous === 'clients') return 'Person details';
        if (previous === 'staff') return 'Staff details';
        if (previous === 'tasks') return 'Task details';
        if (previous === 'instances') return 'Workflow run';
        if (previous === 'care-plans') return 'Care plan';
        if (previous === 'wounds') return 'Wound details';
        return null;
      }
      return SEGMENT_LABELS[segment] ?? sentenceCase(segment);
    })
    .filter((label): label is string => Boolean(label));

  const meaningful = labels.slice(-3);
  return meaningful.length > 0 ? meaningful.join(' - ') : 'Dashboard';
}

export function pageTitleFromPathname(pathname: string | null | undefined): string {
  return `${pageLabelFromPathname(pathname)} | ${APP_TITLE}`;
}
